/**
 * The rent roll.
 *
 * Every live tenancy, what it pays, and what it owes — the schedule an agency prints for a bank,
 * a valuer or an owner. It is a table by nature and it stays one: the only chart is the split of
 * contracted rent between what is settled and what is outstanding, which is the one comparison a
 * reader makes on this page and cannot make by eye down a column of figures.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cycleSuffix, type RentCycle } from "@rentorng/core";
import { api, downloadCsv } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { RankedBars, SEGMENT_COLOURS } from "@/components/charts";
import { Button, Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, ReportHeader, ReportState, useCompactMoney } from "./shared";

interface RentRoll {
  rows: Array<{
    id: string;
    reference: string;
    tenantName: string;
    unitLabel: string;
    propertyName: string;
    propertyId?: string;
    rentAmount: number;
    rentCycle: string;
    startDate: string;
    endDate: string | null;
    balance: number;
  }>;
  contractedRent: number;
  outstanding: number;
  asOf: string;
}

export function RentRollReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-rent-roll"],
    queryFn: () => api.get<RentRoll>("/reports/rent-roll"),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Rent roll" question={`Every live ${vocab.lease}, what it pays and what it owes.`} />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);

  /* Contracted rent grouped by building, from the rows the table below prints — the same array,
     so the chart and the table cannot come apart. */
  const byProperty = new Map<string, number>();
  for (const row of data.rows) byProperty.set(row.propertyName, (byProperty.get(row.propertyName) ?? 0) + row.rentAmount);
  const ranked = [...byProperty.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  return (
    <div>
      <ReportHeader
        title="Rent roll"
        question={`Every live ${vocab.lease}, what it pays and what it owes — the schedule a bank or a valuer asks for.`}
        subtitle={`${data.rows.length} live ${data.rows.length === 1 ? vocab.lease : vocab.leases} · as at ${fmt.date(data.asOf)}`}
        actions={
          <Button variant="secondary" onClick={() => downloadCsv("leases")}>
            Export CSV
          </Button>
        }
      />

      {data.rows.length === 0 ? (
        <Card>
          <EmptyState title={`Nothing is let yet.`} hint={`A ${vocab.lease} appears here the moment it starts.`} />
        </Card>
      ) : (
        <div className="space-y-5">
          <FigureRow>
            <Figure label="Contracted" value={compact(data.contractedRent)} sub="Per cycle, across live tenancies" />
            <Figure
              label="Outstanding"
              value={compact(data.outstanding)}
              tone={data.outstanding > 0 ? "bad" : "good"}
              sub="Raised and not settled"
            />
            <Figure label={`Live ${vocab.leases}`} value={data.rows.length} />
            <Figure
              label="Behind"
              value={data.rows.filter((row) => Number(row.balance) > 0).length}
              tone={data.rows.some((row) => Number(row.balance) > 0) ? "warn" : "good"}
              sub={`Of ${data.rows.length}`}
            />
          </FigureRow>

          <ChartCard
            title={`Contracted ${vocab.rent} by ${vocab.property}`}
            hint="Per cycle, as each tenancy is written. Two buildings on different cycles are not directly comparable, which is why the cycle is on every row of the table."
          >
            <RankedBars
              label={`Contracted rent by ${vocab.property}`}
              rows={ranked}
              format={full}
              colour={SEGMENT_COLOURS.agency}
            />
          </ChartCard>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle>Every live {vocab.lease}</CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Reference</th>
                  <th>{titleCase(vocab.tenant)}</th>
                  <th>{titleCase(vocab.unit)}</th>
                  <th>Term</th>
                  <th className="num">{titleCase(vocab.rent)}</th>
                  <th className="num">Owing</th>
                </tr>
              }
            >
              {data.rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/app/tenancies/${row.id}`} className="link">
                      {row.reference}
                    </Link>
                  </td>
                  <td>{row.tenantName}</td>
                  <td className="text-ink-500">
                    {row.unitLabel}
                    <span className="block text-xs text-ink-400">{row.propertyName}</span>
                  </td>
                  <td className="text-ink-500">
                    {fmt.date(row.startDate)} → {fmt.date(row.endDate)}
                  </td>
                  <td className="num tabular">
                    {full(row.rentAmount)}
                    <span className="block text-xs text-ink-400">{cycleSuffix(row.rentCycle as RentCycle)}</span>
                  </td>
                  <td className="num tabular">
                    {Number(row.balance) > 0 ? (
                      <span className="font-medium text-danger-700">{full(Number(row.balance))}</span>
                    ) : (
                      <span className="text-ink-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="font-medium">
                <td colSpan={4}>Total</td>
                <td className="num tabular">{full(data.contractedRent)}</td>
                <td className="num tabular">{full(data.outstanding)}</td>
              </tr>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
